import { useHistory, useRouteMatch } from 'react-router-dom';
import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import _isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Mixpanel from 'utils/mixpanelService';
import useUserId from 'components/Hooks/useUserId';
import { useSeriesProgress } from 'containers/Series/hooks/useSeriesProgress';
import { windowScrollTo } from 'utils/embedded';
import {
  getResourceName,
  getResourceType,
  trackSeriesCompleted,
  trackSeriesNextClick,
  trackSeriesResourceClicked,
  trackSeriesResourceCompleted,
  trackSeriesResourceCompletedClicked,
} from 'containers/Series/utils';
import {
  completeModuleSeries,
  courseSaveResourceForLater,
  courseSaveSeriesAssessment,
  courseSaveSeriesCompletedResources,
  courseSaveWorksheetProgress,
  enrollCourse,
  saveCourseCustomQuestion,
  updateCourseResource,
} from '../actions';
import { getTakenCourses } from '../selectors';
import {
  getActiveItemIdx,
  getCourseAnswers,
  getActiveTabByResourceType,
  getWorksheetProgress,
  getActiveTab,
  showRecommendedTab,
  shouldCompleteSeries,
  getIntroductionPathname,
} from '../utils';
import {
  COURSE_REFLECT_CUSTOM_QUESTION_KEY,
  COURSE_SAVED_RECOMMENDED_RESOURCE_KEY,
  COURSE_SIDEBAR_TABS,
  SURVEY_TYPES,
} from '../constants';
import { useCourseNavigation } from './useCourseNavigation';

export const useCourse = ({
  item,
  profile,
  takenCoursesProcessing,
  trackingPath,
}) => {
  const dispatch = useDispatch();
  const userId = useUserId();
  const takenCourses = useSelector(getTakenCourses);
  const history = useHistory();
  const [surveyType, setSurveyType] = useState(undefined);

  const isAuthenticated = profile.isLoaded && !profile.isEmpty;
  const collection = isAuthenticated ? 'user_resources' : 'anonymous_courses';
  const hasStartedEnroll = useRef(false);

  const match = useRouteMatch();

  const takenCourse = item ? _get(takenCourses, [item.sys.id], {}) : {};
  const seriesProgress = item
    ? _get(takenCourses[item.sys.id], 'seriesProgress', {})
    : {};
  const isEnrolled = item
    ? _get(takenCourses[item.sys.id], 'enrolled', false)
    : false;
  const completedModulesIds = item
    ? _get(takenCourses[item.sys.id], 'completedModulesIds', [])
    : [];
  const completedSeriesIds = item
    ? _get(takenCourses[item.sys.id], 'completedSeriesIds', [])
    : [];

  const reflectionAnswers = getCourseAnswers(
    takenCourses,
    _get(item, 'sys.id'),
    COURSE_REFLECT_CUSTOM_QUESTION_KEY,
  );
  const completedAssessments =
    item && _get(takenCourses[item.sys.id], 'completedAssessments');

  const isCourseCompleted =
    !_isEmpty(completedModulesIds) &&
    _get(item, 'fields.modules', []).every(m =>
      completedModulesIds.includes(m.sys.id),
    );

  const {
    continueCourse,
    onModuleClick,
    activeModuleIdx,
    activeSeriesIdx,
    activeResourceIdx,
    activeModule,
    activeSeries,
    activeSeriesResources,
    activeResource,
    activeTab,
    setActiveSeriesIdx,
    setActiveResourceIdx,
    setActiveTab,
    setActiveModuleIdx,
    getNextResourceIndexes,
    goToResource,
    isLastModule,
  } = useCourseNavigation({
    item,
    takenCourses,
    takenCoursesProcessing,
    isEnrolled,
    completedModulesIds,
    trackingPath,
  });

  useEffect(() => {
    if (!hasStartedEnroll.current || !isEnrolled) return;

    if (isCourseCompleted) {
      history.replace(getIntroductionPathname(match, true));
    } else {
      continueCourse();
      setSurveyType(SURVEY_TYPES.PRE);
    }

    hasStartedEnroll.current = false;
  }, [isEnrolled, isCourseCompleted]);

  const completedResourcesIds =
    seriesProgress && activeSeries
      ? _get(seriesProgress, [activeSeries.sys.id, 'completedResourcesIds'], [])
      : [];
  const resourcesProgress =
    item && activeSeries
      ? _get(takenCourses[item.sys.id], [
          'seriesProgress',
          activeSeries.sys.id,
          'resourcesProgress',
        ])
      : null;
  const worksheetProgress = getWorksheetProgress(
    takenCourses,
    _get(item, 'sys.id'),
    _get(activeResource, 'sys.id'),
  );

  const closeSurvey = () => {
    setSurveyType(undefined);
  };

  const updateCompletedResources = (resource, completed) => {
    const nextCompletedResourceIds = completed
      ? [...completedResourcesIds, resource.sys.id]
      : completedResourcesIds.filter(id => id !== resource.sys.id);

    dispatch(
      courseSaveSeriesCompletedResources({
        userId,
        id: item.sys.id,
        moduleId: activeModule.sys.id,
        seriesId: activeSeries.sys.id,
        resourceId: resource.sys.id,
        collection,
        isResourceCompleted: completed,
        completedResourcesIds: nextCompletedResourceIds,
      }),
    );
  };

  // If all resources in the series are completed
  // and the series is not completed, mark the series as completed
  useEffect(() => {
    if (
      !_isEmpty(activeSeriesResources) &&
      !completedSeriesIds.includes(_get(activeSeries, 'sys.id')) &&
      activeSeriesResources.every(r => completedResourcesIds.includes(r.sys.id))
    ) {
      onSeriesComplete(false, false, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedResourcesIds]);

  const handleCompleteChange = (resource, completed) => {
    updateCompletedResources(resource, completed);
    trackSeriesResourceCompletedClicked({
      item: activeSeries,
      resource,
      completed,
      trackingPath,
      module: _get(activeModule, 'fields.title'),
      section: _get(activeSeries, 'fields.title'),
      name: undefined,
    });
  };

  const completeResource = (
    resource,
    completed,
    { autoCompleteSeries = false, shouldTransition = false } = {},
  ) => {
    const isLastCompleted =
      completed &&
      shouldCompleteSeries({
        resource,
        resources: activeSeriesResources,
        completedResourcesIds,
      });

    trackSeriesResourceCompleted({
      item: activeSeries,
      resource,
      trackingPath,
      module: _get(activeModule, 'fields.title'),
      section: _get(activeSeries, 'fields.title'),
      name: undefined,
    });

    if (autoCompleteSeries && isLastCompleted) {
      onSeriesComplete(shouldTransition);
      return;
    }

    if (!completedResourcesIds.includes(resource.sys.id)) {
      updateCompletedResources(resource, completed);
    }
    if (shouldTransition) {
      handleNext(activeResourceIdx, completed);
    }
  };

  const completeSeriesAssessment = (resource, accessCode) => {
    dispatch(
      courseSaveSeriesAssessment({
        item,
        accessCode,
        seriesId: activeSeries.sys.id,
        assessmentId: resource.sys.id,
        userId,
        collection,
      }),
    );

    completeResource(resource, true, {
      autoCompleteSeries: false,
      shouldTransition: false,
    });
  };

  const updateResourceProgress = useCallback(
    (seriesId, resourceId, seconds) => {
      dispatch(
        updateCourseResource({
          id: item.sys.id,
          moduleId: activeModule.sys.id,
          seriesId,
          userId,
          resourceId,
          collection,
          isResourceCompleted: false,
          resourcesProgress: {
            [resourceId]: seconds,
          },
        }),
      );
    },
    [item, activeModule, collection, userId],
  );

  const { onProgressUpdate } = useSeriesProgress({
    completedResourcesIds,
    resources: _get(activeSeries, 'fields.resources', []),
    completeResource,
    updateResourceProgress,
  });

  const handleLastViewedResource = ({
    series,
    resource,
    module,
    isResourceCompleted,
  }) => {
    dispatch(
      updateCourseResource({
        userId,
        id: item.sys.id,
        moduleId: module.sys.id,
        seriesId: series.sys.id,
        resourceId: resource.sys.id,
        collection,
        isResourceCompleted,
      }),
    );
  };

  const onResourceClick = (seriesIndex, resourceIndex, isResourceCompleted) => {
    const series = _get(activeModule, [
      'fields',
      'moduleResources',
      seriesIndex,
    ]);
    const seriesResources = _get(series, 'fields.resources', []);
    const tab = getActiveTabByResourceType(seriesResources[resourceIndex]);

    setActiveTab(tab);
    setActiveSeriesIdx(seriesIndex);
    setActiveResourceIdx(resourceIndex);
    handleLastViewedResource({
      series,
      resource: seriesResources[resourceIndex],
      module: activeModule,
      isResourceCompleted,
    });

    trackSeriesResourceClicked({
      item: series,
      resource: seriesResources[resourceIndex],
      trackingPath,
      module: _get(activeModule, 'fields.title'),
      section: _get(series, 'fields.title'),
      name: undefined,
    });
  };

  const completeWelcomeSeries = () => {
    setActiveTab(COURSE_SIDEBAR_TABS.RESOURCE);
    setActiveResourceIdx(0);
    onSeriesComplete(false);
  };

  const onSeriesComplete = (
    shouldTransition = true,
    shouldScrollToTop = true,
    isCompleted = true,
  ) => {
    const moduleSeries = _get(activeModule, 'fields.moduleResources', []);
    const courseModules = _get(item, ['fields', 'modules']);
    const nextCompletedSeriesIds = _uniq(
      isCompleted
        ? [...completedSeriesIds, activeSeries.sys.id]
        : completedSeriesIds.filter(id => id !== activeSeries.sys.id),
    );

    const isModuleSeriesCompleted = moduleSeries.every(series =>
      nextCompletedSeriesIds.includes(series.sys.id),
    );
    const isLastCompleted =
      !!activeResource && completedResourcesIds.includes(activeResource.sys.id);

    // If all series in the module are completed, add the module to the completedModulesIds
    // otherwise, remove the module from the completedModulesIds
    const nextCompletedModulesIds = _uniq(
      isModuleSeriesCompleted
        ? [...completedModulesIds, activeModule.sys.id]
        : completedModulesIds.filter(id => id !== activeModule.sys.id),
    );
    const nextCompletedResourcesIds =
      isLastCompleted || !activeResource
        ? completedResourcesIds
        : [...completedResourcesIds, activeResource.sys.id];

    // If the series is completed, we should move to the next series
    // otherwise, we should stay on the same series
    const nextSeriesIdx = isCompleted
      ? getActiveItemIdx({
          id: activeSeries.sys.id,
          completedIds: nextCompletedSeriesIds,
          items: moduleSeries,
        })
      : activeSeriesIdx;

    if (shouldScrollToTop && isCompleted) windowScrollTo(0, 0);

    if (shouldTransition && isCompleted) {
      handleNext(nextSeriesIdx, isLastCompleted, {
        completedModulesIds: nextCompletedModulesIds,
        completedSeriesIds: nextCompletedSeriesIds,
      });
    }

    dispatch(
      completeModuleSeries({
        userId,
        item,
        moduleId: activeModule.sys.id,
        seriesId: activeSeries.sys.id,
        nextSeriesId: _get(moduleSeries, [nextSeriesIdx, 'sys', 'id']),
        collection,
        completedResourcesIds: nextCompletedResourcesIds,
        completedModulesIds: nextCompletedModulesIds,
        completedSeriesIds: nextCompletedSeriesIds,
        isCompleted,
      }),
    );

    trackSeriesCompleted({
      item: activeSeries,
      trackingPath,
      module: _get(activeModule, 'fields.title'),
      section: _get(activeSeries, 'fields.title'),
      name: undefined,
    });
    if (courseModules.every(m => nextCompletedModulesIds.includes(m.sys.id))) {
      Mixpanel.track('Course - Complete - Clicked', {
        course: item.fields.title,
        path: trackingPath,
      });
    }
  };

  const onEnrollCourse = () => {
    Mixpanel.track('Course - Start - Clicked', {
      course: item.fields.title,
      path: trackingPath,
    });
    hasStartedEnroll.current = true;
    dispatch(
      enrollCourse({
        item,
        userId,
        collection,
        courseId: _get(item, 'sys.id'),
        isAuthenticated,
      }),
    );
  };

  const onRecommendedTabClick = seriesIdx => {
    setActiveTab(COURSE_SIDEBAR_TABS.RECOMMENDED);
    setActiveResourceIdx(null);
    setActiveSeriesIdx(seriesIdx);
  };

  const handleNext = (_idx, completed, nextIds) => {
    const { moduleIdx, seriesIdx, resourceIdx } = getNextResourceIndexes(
      nextIds,
    );

    // If course is completed and all indexes are 0, redirect to dashboard
    const shouldNavigateToDashboard =
      isCourseCompleted &&
      moduleIdx === 0 &&
      seriesIdx === 0 &&
      resourceIdx === 0;

    if (shouldNavigateToDashboard) {
      setSurveyType(SURVEY_TYPES.POST);
      history.push(getIntroductionPathname(match, true));
      return;
    }

    // If the last series has recommended tab and now we're switching to next series
    // then we should move to the recommended tab

    const isSwitchingToNextSeries = seriesIdx !== activeSeriesIdx;
    const shouldShowRecommendedTab =
      isSwitchingToNextSeries &&
      showRecommendedTab(activeSeries, activeResource) &&
      activeTab !== COURSE_SIDEBAR_TABS.RECOMMENDED;

    if (activeSeries && activeResource) {
      trackSeriesNextClick({
        item: activeSeries,
        resource: activeResource,
        trackingPath,
        completed,
        module: _get(activeModule, 'fields.title'),
        section: _get(activeSeries, 'fields.title'),
        name: undefined,
      });
    }

    if (shouldShowRecommendedTab) {
      setActiveTab(COURSE_SIDEBAR_TABS.RECOMMENDED);
      windowScrollTo(0, 0);
      return;
    }

    const nextModule = _get(item, ['fields', 'modules', moduleIdx]);
    const nextSeries = _get(nextModule, [
      'fields',
      'moduleResources',
      seriesIdx,
    ]);
    const nextResource = _get(nextSeries, ['fields', 'resources', resourceIdx]);
    const nextTab = getActiveTab(nextSeries, nextResource);

    setActiveModuleIdx(moduleIdx);
    setActiveSeriesIdx(seriesIdx);
    setActiveResourceIdx(resourceIdx);
    setActiveTab(nextTab);

    windowScrollTo(0, 0);
    history.push(`${nextModule.fields.slug}-module`);
  };

  const saveAnswer = (question, field) => {
    dispatch(
      saveCourseCustomQuestion({
        item,
        userId,
        collection,
        question,
        field,
      }),
    );
  };

  const saveWorksheetProgress = (answers, stepsPayload) => {
    dispatch(
      courseSaveWorksheetProgress({
        item,
        userId,
        collection,
        answers,
        id: activeResource.sys.id,
        stepsPayload,
      }),
    );
  };

  const saveResourceForLater = ({
    resourceId,
    isRemove,
    field = COURSE_SAVED_RECOMMENDED_RESOURCE_KEY,
  }) => {
    dispatch(
      courseSaveResourceForLater({
        userId,
        resourceId,
        collection,
        courseId: _get(item, 'sys.id'),
        isRemove,
        field,
      }),
    );
  };

  const removeResourceFromLater = ({ resource, field }) => {
    saveResourceForLater({
      resourceId: resource.sys.id,
      isRemove: true,
      field,
    });

    Mixpanel.track('Course Dashboard - Resource - Removed', {
      course: item.fields.title,
      type: getResourceType(resource),
      name: getResourceName(resource),
    });
  };

  const onSeriesClick = (series, seriesIdx) => {
    const resource = _get(series, ['fields', 'resources', 0]);
    const tab = getActiveTab(series, resource);
    if (activeSeries === series && activeTab === tab) {
      return;
    }

    setActiveResourceIdx(0);
    setActiveTab(tab);
    setActiveSeriesIdx(seriesIdx);
  };

  return {
    isAuthenticated,
    activeSeriesIdx,
    activeResourceIdx,
    activeModuleIdx,
    activeModule,
    activeSeries,
    activeResource,
    getNextResourceIndexes,
    takenCourse,
    onResourceClick,
    onSeriesComplete,
    onRecommendedTabClick,
    handleNext,
    handleCompleteChange,
    completeResource,
    completedResourcesIds,
    seriesProgress,
    activeTab,
    onEnrollCourse,
    completedModulesIds,
    onModuleClick,
    completedSeriesIds,
    onProgressUpdate,
    resourcesProgress,
    saveAnswer,
    onSeriesClick,
    reflectionAnswers,
    isEnrolled,
    continueCourse,
    completeWelcomeSeries,
    completedAssessments,
    completeSeriesAssessment,
    saveResourceForLater,
    removeResourceFromLater,
    goToResource,
    saveWorksheetProgress,
    worksheetProgress,
    isLastModule,
    surveyType,
    closeSurvey,
  };
};
